/**
 * 회원가입 온보딩 — 방식 A (가이드 마법사 + LLM 0회).
 *
 * 단계: 환영 → 이름 → 직업 → 핵심(주 연락상대 + 중요 액션) → 권한 → 통화녹음 폴더 → 완료
 *
 * 직군 무관 질문으로 profile_json을 클라이언트에서 구성해 /complete로 저장한다(LLM·web_search 없음).
 * profile_json은 모든 다운스트림 LLM 호출의 system 프롬프트에 주입돼 요약·TODO·채팅을 그 직군 언어로 튜닝.
 * (대화형 "AI 와우 모먼트" 온보딩은 v1.5+에서 발전 — project_butler_onboarding 메모리 참조.)
 *
 * "건너뛰기" 항상 우상단. 건너뛰기 시 onboarded_at=now + profile_json={skipped:true}.
 * 디자인: index.css design 토큰만 사용. 호칭은 이름 기반(없으면 중립).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { PermissionGroup } from '@butler/shared';
import { api, type RecordingFolder, type ScannedFolderCandidate } from '../lib/api';

type Step = 'welcome' | 'name' | 'occupation' | 'focus' | 'permissions' | 'folders' | 'done';

const STEPS: Step[] = ['welcome', 'name', 'occupation', 'focus', 'permissions', 'folders', 'done'];

const CONTACT_OPTIONS = ['고객', '거래처', '내부 직원', '의뢰인·환자', '협력사'];
const ACTION_OPTIONS = ['약속·미팅', '견적·계약', '청약·보장', '예약', '리마인드·팔로업', '입금·정산'];

const PERMISSION_CARDS: Array<{
  group: PermissionGroup;
  title: string;
  desc: string;
  required: boolean;
}> = [
  {
    group: 'recording',
    title: '통화 녹음 및 통화 기록',
    desc: '통화 내용을 자동으로 요약하고 할 일을 추출하기 위해 필요합니다.',
    required: true,
  },
  {
    group: 'sms',
    title: '문자 메시지',
    desc: '문자에 숨어있는 약속·요청을 놓치지 않도록 합니다.',
    required: false,
  },
  {
    group: 'contacts',
    title: '연락처',
    desc: '"김 차장"이 누구인지 Butler가 알아보게 합니다.',
    required: false,
  },
  {
    group: 'notifications',
    title: '알림',
    desc: '중요한 할 일, 미응답 통화를 알려드립니다.',
    required: false,
  },
];

type PermStatus = 'idle' | 'requesting' | 'granted' | 'denied';

export function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('welcome');
  const [name, setName] = useState('');
  const [occupation, setOccupation] = useState('');
  const [contacts, setContacts] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [savingFinal, setSavingFinal] = useState(false);
  const [permStatus, setPermStatus] = useState<Record<PermissionGroup, PermStatus>>({
    recording: 'idle',
    sms: 'idle',
    contacts: 'idle',
    notifications: 'idle',
  });
  const [folderScanning, setFolderScanning] = useState(false);
  const [folderScanned, setFolderScanned] = useState(false);
  const [folderCandidates, setFolderCandidates] = useState<ScannedFolderCandidate[]>([]);
  const [folderSavingErr, setFolderSavingErr] = useState<string | null>(null);

  const stepIdx = STEPS.indexOf(step);

  // ----- 권한 이벤트 listener -----------------------------------------------------------
  useEffect(() => {
    function onPerm(e: Event) {
      const ev = e as CustomEvent<{ name: PermissionGroup; granted: boolean }>;
      const { name: g, granted } = ev.detail;
      setPermStatus((s) => ({ ...s, [g]: granted ? 'granted' : 'denied' }));
      if (granted) triggerBackfillFor(g);
    }
    window.addEventListener('butler:permission-changed', onPerm);
    return () => window.removeEventListener('butler:permission-changed', onPerm);
  }, []);

  function next() {
    const i = STEPS.indexOf(step);
    if (i < 0) return;
    const nxt = STEPS[i + 1];
    if (nxt) setStep(nxt);
  }

  function toggle(list: string[], set: (v: string[]) => void, item: string) {
    set(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  }

  // ----- 이름 저장 ---------------------------------------------------------------------
  const patchUserM = useMutation({
    mutationFn: (body: { name?: string }) => api.patchUsersMe(body),
  });

  async function onNameNext() {
    if (!name.trim()) return;
    try {
      await patchUserM.mutateAsync({ name: name.trim() });
    } catch (err) {
      console.error(err);
    }
    next();
  }

  // ----- 권한 요청 + 백필 트리거 -------------------------------------------------------
  function requestPerm(group: PermissionGroup) {
    setPermStatus((s) => ({ ...s, [group]: 'requesting' }));
    const w = window.Butler;
    if (!w?.requestPermission) {
      setTimeout(() => setPermStatus((s) => ({ ...s, [group]: 'granted' })), 300);
      return;
    }
    try {
      const started = w.requestPermission(group);
      if (!started) {
        setPermStatus((s) => ({ ...s, [group]: 'granted' }));
        triggerBackfillFor(group);
      }
    } catch (err) {
      console.error('[signup] requestPermission failed', err);
      setPermStatus((s) => ({ ...s, [group]: 'denied' }));
    }
  }

  function triggerBackfillFor(group: PermissionGroup) {
    const w = window.Butler;
    if (!w) return;
    try {
      if (group === 'sms') {
        const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
        w.syncSms?.(since);
      } else if (group === 'contacts') {
        w.syncContacts?.();
      }
    } catch (err) {
      console.error('[signup] backfill trigger failed', err);
    }
  }

  function onPermissionsNext() {
    // 녹음 권한 없으면 folders 단계 스킵하고 완료로
    if (permStatus.recording !== 'granted') {
      void finalize();
      return;
    }
    next();
  }

  // ----- 폴더 스캔 ---------------------------------------------------------------------
  useEffect(() => {
    if (step !== 'folders' || folderScanned) return;
    setFolderScanned(true);
    void runFolderScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, folderScanned]);

  async function runFolderScan() {
    const native = window.Butler;
    if (!native?.scanRecordingFolders) {
      setFolderCandidates([]);
      return;
    }
    setFolderScanning(true);
    try {
      const json = native.scanRecordingFolders();
      const list = JSON.parse(json) as ScannedFolderCandidate[];
      setFolderCandidates(list);
    } catch (err) {
      console.error('[signup] folder scan failed', err);
      setFolderCandidates([]);
    } finally {
      setFolderScanning(false);
    }
  }

  async function addFolderCandidate(c: ScannedFolderCandidate) {
    setFolderSavingErr(null);
    try {
      const existing = await api.getRecordingFolders();
      if (existing.folders.some((f) => f.path === c.path)) {
        setFolderCandidates((prev) => prev.filter((x) => x.path !== c.path));
        return;
      }
      const updated: RecordingFolder[] = [
        ...existing.folders,
        { path: c.path, source: 'auto', enabled: true, discovered_at: new Date().toISOString() },
      ];
      await api.setRecordingFolders(updated);
      try {
        window.Butler?.setRecordingFolders?.(JSON.stringify(updated));
      } catch {
        /* no-op */
      }
      setFolderCandidates((prev) => prev.filter((x) => x.path !== c.path));
    } catch (err) {
      setFolderSavingErr(err instanceof Error ? err.message : String(err));
    }
  }

  // ----- profile_json 구성 + 저장 (LLM 없음) -------------------------------------------
  function buildProfile(): Record<string, unknown> {
    return {
      name: name.trim() || null,
      occupation: occupation.trim() || null,
      primary_contacts: contacts,
      priority_actions: actions,
      source: 'guided_v1',
    };
  }

  async function finalize() {
    setSavingFinal(true);
    try {
      await api.onboardingComplete({ profile: buildProfile() });
    } catch (err) {
      console.error('[signup] complete failed', err);
    } finally {
      setSavingFinal(false);
      setStep('done');
    }
  }

  async function onSkip() {
    try {
      if (name.trim()) await patchUserM.mutateAsync({ name: name.trim() });
    } catch {
      /* 무시 */
    }
    try {
      await api.onboardingComplete({ profile: { skipped: true, name: name.trim() || null } });
    } catch {
      /* 무시 */
    }
    navigate('/', { replace: true });
  }

  // ----- 진행 dots ---------------------------------------------------------------------
  const dots = useMemo(
    () =>
      STEPS.map((s, i) => (
        <span
          key={s}
          className={`w-[6px] h-[6px] rounded-full ${
            i <= stepIdx ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'
          }`}
        />
      )),
    [stepIdx],
  );

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)]">
      <header className="flex items-center justify-between px-xl pt-2xl pb-md">
        <div className="flex items-center gap-xs">{dots}</div>
        {step !== 'done' && (
          <button
            type="button"
            onClick={onSkip}
            className="text-[12px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
          >
            건너뛰기
          </button>
        )}
      </header>

      <main className="flex-1 px-xl pb-xl flex flex-col">
        {step === 'welcome' && (
          <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
            <h1 className="font-display text-[28px] font-medium leading-tight text-[var(--text-primary)] mb-md">
              환영합니다.
            </h1>
            <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed mb-2xl">
              더 정확한 비서가 되기 위해 몇 가지만 알려주세요. 30초면 충분해요.
            </p>
            <button
              type="button"
              onClick={next}
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium transition-colors"
            >
              시작하기
            </button>
            <p className="text-[11px] text-[var(--text-muted)] mt-md leading-relaxed">
              이 정보는 Butler가 더 정확한 비서가 되기 위해서만 사용됩니다. 외부 학습에 쓰이지 않습니다.
            </p>
          </div>
        )}

        {step === 'name' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onNameNext();
            }}
            className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full"
          >
            <h2 className="font-display text-[22px] font-medium text-[var(--text-primary)] mb-md leading-tight">
              어떻게 불러드릴까요?
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] mb-xl">
              앞으로 Butler가 부를 호칭에 사용됩니다.
            </p>
            <label className="block mb-lg">
              <span className="block text-[12px] uppercase tracking-wider text-[var(--text-muted)] mb-xs font-semibold">
                이름
              </span>
              <input
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="이름"
                className="w-full px-md py-[12px] rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                maxLength={40}
                required
              />
            </label>
            <button
              type="submit"
              disabled={!name.trim() || patchUserM.isPending}
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium disabled:opacity-50 transition-colors"
            >
              {patchUserM.isPending ? '저장 중…' : '다음'}
            </button>
          </form>
        )}

        {step === 'occupation' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              next();
            }}
            className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full"
          >
            <h2 className="font-display text-[22px] font-medium text-[var(--text-primary)] mb-md leading-tight">
              {name ? `${name}님은` : ''} 어떤 일을 하세요?
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] mb-xl leading-relaxed">
              하는 일을 알면 통화·문자 요약을 그 일에 맞게 정리해 드려요.
            </p>
            <label className="block mb-lg">
              <span className="block text-[12px] uppercase tracking-wider text-[var(--text-muted)] mb-xs font-semibold">
                하는 일
              </span>
              <input
                type="text"
                autoFocus
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                placeholder="예: 보험설계사, 건축자재 영업, 공인중개사, 카페 운영"
                className="w-full px-md py-[12px] rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                maxLength={80}
              />
            </label>
            <button
              type="submit"
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium transition-colors"
            >
              다음
            </button>
          </form>
        )}

        {step === 'focus' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              next();
            }}
            className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full"
          >
            <h2 className="font-display text-[22px] font-medium text-[var(--text-primary)] mb-md leading-tight">
              무엇을 제일 챙겨드릴까요?
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] mb-lg leading-relaxed">
              해당하는 걸 골라주세요. 통화·문자에서 이것부터 놓치지 않게 챙길게요.
            </p>

            <span className="block text-[12px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
              주로 누구와 연락하세요?
            </span>
            <div className="flex flex-wrap gap-sm mb-lg">
              {CONTACT_OPTIONS.map((opt) => (
                <Chip key={opt} label={opt} on={contacts.includes(opt)} onClick={() => toggle(contacts, setContacts, opt)} />
              ))}
            </div>

            <span className="block text-[12px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
              제일 놓치면 안 되는 것
            </span>
            <div className="flex flex-wrap gap-sm mb-xl">
              {ACTION_OPTIONS.map((opt) => (
                <Chip key={opt} label={opt} on={actions.includes(opt)} onClick={() => toggle(actions, setActions, opt)} />
              ))}
            </div>

            <button
              type="submit"
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium transition-colors"
            >
              다음
            </button>
          </form>
        )}

        {step === 'permissions' && (
          <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
            <h2 className="font-display text-[22px] font-medium text-[var(--text-primary)] mb-xs leading-tight">
              Butler가 일을 시작하려면…
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] mb-lg">
              아래 권한을 허용해주세요. 거부하셔도 나중에 "더보기"에서 켤 수 있습니다.
            </p>

            <div className="space-y-sm flex-1">
              {PERMISSION_CARDS.map((card) => (
                <PermCard
                  key={card.group}
                  title={card.title}
                  desc={card.desc}
                  required={card.required}
                  status={permStatus[card.group]}
                  onRequest={() => requestPerm(card.group)}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={onPermissionsNext}
              disabled={savingFinal}
              className="w-full mt-lg py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium disabled:opacity-50 transition-colors"
            >
              {savingFinal ? '저장 중…' : '다음'}
            </button>
            <p className="text-[11px] text-[var(--text-muted)] mt-sm leading-relaxed text-center">
              권한을 허용하면 지난 데이터부터 천천히 동기화합니다.
            </p>
          </div>
        )}

        {step === 'folders' && (
          <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
            <h2 className="font-display text-[22px] font-medium text-[var(--text-primary)] mb-xs leading-tight">
              통화녹음 폴더를 찾고 있어요.
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] mb-lg leading-relaxed">
              기종마다 저장 위치가 달라요. Butler가 자동으로 찾을게요.
            </p>

            {folderScanning && (
              <div className="flex items-center gap-sm text-[13px] text-[var(--text-secondary)] py-md">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
                <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
                <span className="ml-xs">폴더를 찾는 중…</span>
              </div>
            )}

            {!folderScanning && folderCandidates.length > 0 && (
              <div className="space-y-sm flex-1">
                <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
                  발견된 폴더 ({folderCandidates.length})
                </p>
                {folderCandidates.map((c) => (
                  <div
                    key={c.path}
                    className="p-md rounded-lg border border-[var(--border)] bg-[var(--surface)] flex items-start gap-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-mono text-[var(--text-primary)] truncate" title={c.path}>
                        {c.path}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)] mt-[2px]">
                        {c.confidence === 'high'
                          ? '신뢰도 높음'
                          : c.confidence === 'medium'
                            ? '신뢰도 보통'
                            : '신뢰도 낮음'}
                        {' · '}샘플 {c.sample_count}건
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => addFolderCandidate(c)}
                      className="shrink-0 px-md py-[6px] rounded-md bg-[var(--accent)] text-[var(--accent-on)] text-[12px] font-medium hover:bg-[var(--accent-press)]"
                    >
                      추가
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!folderScanning && folderCandidates.length === 0 && folderScanned && (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-xl">
                <p className="text-[14px] text-[var(--text-primary)] mb-sm">자동으로 찾지 못했어요.</p>
                <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed max-w-[280px]">
                  통화를 한 번 한 뒤 "더보기 → 통화녹음 폴더"에서 다시 시도하시거나, 직접 폴더를 추가하실 수
                  있어요.
                </p>
                <button
                  type="button"
                  onClick={() => void runFolderScan()}
                  className="mt-md text-[12px] px-md py-[8px] rounded-md border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  다시 탐색
                </button>
              </div>
            )}

            {folderSavingErr && (
              <p className="text-[12px] text-[var(--error)] bg-[var(--surface-elev)] border border-[var(--border)] rounded-md p-md mt-md">
                저장 실패: {folderSavingErr}
              </p>
            )}

            <button
              type="button"
              onClick={() => void finalize()}
              disabled={savingFinal}
              className="w-full mt-lg py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium disabled:opacity-50 transition-colors"
            >
              {savingFinal ? '저장 중…' : '다음'}
            </button>
            <p className="text-[11px] text-[var(--text-muted)] mt-sm leading-relaxed text-center">
              나중에 "더보기"에서 언제든지 변경할 수 있어요.
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
            <div className="w-[40px] h-[40px] rounded-full bg-[var(--accent-tint)] border border-[var(--accent)] mb-lg" />
            <h2 className="font-display text-[24px] font-medium text-[var(--text-primary)] mb-md leading-tight">
              {name ? `${name}님, 준비됐어요.` : '준비됐어요.'}
            </h2>
            <p className="text-[14px] text-[var(--text-secondary)] leading-relaxed mb-2xl">
              {occupation ? `"${occupation}" 일에 맞춰 ` : ''}지금부터 통화·문자·일정을 더 정확하게 정리해
              드릴게요.
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium transition-colors"
            >
              메인으로
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-md py-[8px] rounded-full text-[13px] border transition-colors ${
        on
          ? 'border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--text-primary)]'
          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
      }`}
    >
      {label}
    </button>
  );
}

function PermCard({
  title,
  desc,
  required,
  status,
  onRequest,
}: {
  title: string;
  desc: string;
  required: boolean;
  status: PermStatus;
  onRequest: () => void;
}) {
  const granted = status === 'granted';
  const denied = status === 'denied';
  const busy = status === 'requesting';
  return (
    <div
      className={`p-md rounded-lg border transition-colors ${
        granted
          ? 'border-[var(--accent)] bg-[var(--accent-tint)]'
          : denied
            ? 'border-[var(--error)] bg-[var(--surface)]'
            : 'border-[var(--border)] bg-[var(--surface)]'
      }`}
    >
      <div className="flex items-start justify-between gap-sm">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-xs">
            <span className="text-[14px] font-medium text-[var(--text-primary)]">{title}</span>
            {required && (
              <span className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-semibold">필수</span>
            )}
          </div>
          <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed mt-[2px]">{desc}</p>
          {denied && (
            <p className="text-[11px] text-[var(--error)] mt-xs">거부됨 — 설정에서 직접 허용해야 합니다.</p>
          )}
        </div>
        <button
          type="button"
          onClick={onRequest}
          disabled={granted || busy}
          className={`shrink-0 px-md py-[6px] rounded-md text-[12px] font-medium transition-colors ${
            granted
              ? 'bg-transparent text-[var(--accent)] cursor-default'
              : 'bg-[var(--accent)] text-[var(--accent-on)] hover:bg-[var(--accent-press)] disabled:opacity-50'
          }`}
        >
          {granted ? '허용됨' : busy ? '요청 중…' : denied ? '재요청' : '허용'}
        </button>
      </div>
    </div>
  );
}
